import { useParams, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { articleContent } from '@/mocks/articleContent';
import { articles } from '@/mocks/articles';

const pathToSlug: Record<string, string> = {
  'allchinabuy-spreadsheet': 'allchinabuy-spreadsheet-2026-guide',
  'allchinabuy-review': 'allchinabuy-review-2026',
  'allchinabuy-shipping-cost': 'allchinabuy-shipping-cost-guide',
  'allchinabuy-coupon': 'allchinabuy-coupon-discount-guide',
  'allchinabuy-warehouse': 'allchinabuy-warehouse-guide',
  'allchinabuy-qc-guide': 'allchinabuy-qc-guide',
  'allchinabuy-beginner-guide': 'allchinabuy-beginner-guide',
  'is-allchinabuy-legit': 'is-allchinabuy-legit',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const currentLang = i18n.language || 'en';

  const resolvedSlug = slug || pathToSlug[location.pathname.replace('/', '')] || '';
  const article = articles.find((a) => a.slug === resolvedSlug);
  const content = articleContent[resolvedSlug];

  useEffect(() => {
    setLoaded(true);
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo(0, 0);
    html.style.scrollBehavior = prevBehavior;
  }, [resolvedSlug]);

  if (!article || !content) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-stone-900 mb-4">Article not found</h1>
          <Link to="/" className="text-stone-600 underline">Back to home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = currentLang === 'fr' ? content.titleFr : currentLang === 'da' ? content.titleDa : currentLang === 'de' ? content.titleDe : content.titleEn;
  const paragraphs = currentLang === 'fr' ? content.contentFr : currentLang === 'da' ? content.contentDa : currentLang === 'de' ? content.contentDe : content.contentEn;
  const metaDesc = currentLang === 'fr' ? content.metaDescriptionFr : currentLang === 'da' ? content.metaDescriptionDa : currentLang === 'de' ? content.metaDescriptionDe : content.metaDescriptionEn;

  const relatedArticles = articles.filter((a) => a.slug !== resolvedSlug).slice(0, 3);

  const getRelatedTitle = (rel: typeof articles[0]) => {
    switch (currentLang) {
      case 'fr': return rel.titleFr;
      case 'da': return rel.titleDa;
      case 'de': return rel.titleDe;
      default: return rel.titleEn;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-stone-50 border-b border-stone-200">
        <div className="w-full px-6 lg:px-10 max-w-5xl mx-auto">
          <nav className="flex items-center gap-2 text-sm text-stone-500">
            <Link to="/" className="hover:text-stone-900 transition-colors">{t('common.breadcrumbHome')}</Link>
            <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center text-xs" />
            <span className="text-stone-900">{title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <header className="bg-stone-50 pt-10 pb-12">
        <div className={`w-full px-6 lg:px-10 max-w-5xl mx-auto transition-all duration-700 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <span className="text-xs font-semibold text-stone-500 uppercase tracking-wider">{article.category}</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mt-3 leading-tight" style={{ fontFamily: 'var(--font-heading, serif)' }}>
            {title}
          </h1>
          <p className="text-base text-stone-500 mt-4 max-w-2xl">{metaDesc}</p>
        </div>
      </header>

      {/* Hero Image */}
      <div className="w-full max-w-5xl mx-auto px-6 lg:px-10">
        <div className="relative h-64 md:h-96 rounded-xl overflow-hidden">
          <img
            src={article.image}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      {/* Article Body */}
      <article className="w-full px-6 lg:px-10 max-w-3xl mx-auto py-14">
        <div className="prose prose-stone max-w-none">
          {paragraphs.map((para, idx) => (
            <p
              key={idx}
              className="text-base leading-relaxed text-stone-700 mb-6"
              dangerouslySetInnerHTML={{ __html: para }}
            />
          ))}
        </div>

        {/* CTA within article */}
        <div className="mt-12 p-8 bg-stone-50 rounded-xl border border-stone-200">
          <h3 className="text-lg font-bold text-stone-900 mb-2" style={{ fontFamily: 'var(--font-heading, serif)' }}>
            Ready to start shopping?
          </h3>
          <p className="text-sm text-stone-500 mb-4">
            Visit the main AllChinaBuy website to browse the full product catalog and place your order.
          </p>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-6 py-3 bg-stone-900 text-white rounded-full text-sm font-medium hover:bg-stone-800 transition-all whitespace-nowrap"
          >
            <i className="ri-external-link-line w-4 h-4 flex items-center justify-center" />
            AllChinaBuy Spreadsheet
          </a>
        </div>
      </article>

      {/* Related Articles */}
      <section className="bg-stone-50 py-16 border-t border-stone-200">
        <div className="w-full px-6 lg:px-10 max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-stone-900 mb-8" style={{ fontFamily: 'var(--font-heading, serif)' }}>
            Related Guides
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedArticles.map((rel) => (
              <Link
                key={rel.slug}
                to={`/article/${rel.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-stone-200 hover:border-stone-300 transition-all hover:shadow-md"
              >
                <div className="h-40 overflow-hidden">
                  <img
                    src={rel.image}
                    alt={getRelatedTitle(rel)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">{rel.category}</span>
                  <h3 className="text-sm font-bold text-stone-900 mt-2 group-hover:text-stone-700 transition-colors" style={{ fontFamily: 'var(--font-heading, serif)' }}>
                    {getRelatedTitle(rel)}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}